/**
 * ShowdownCard — detailed public-facing game card on the landing page.
 *
 * Props: showdown (game_slates doc with id attached)
 * Exported: computePublicStatus (used by UpcomingShowdownsSection for sorting)
 */
import React, { useState, useEffect, useRef } from 'react';
import { getShowdownDisplayState } from '../utils/showdownStatus';

// ── Mobile scroll-highlight hook ──────────────────────────────────────────────
// Applies gold border to the card centred in the viewport on mobile only.
// On desktop the existing :hover CSS handles highlighting — this hook is a no-op.
function useMobileInView() {
  const ref    = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '-25% 0px -25% 0px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
}

// ── Timestamp helper ──────────────────────────────────────────────────────────

export function toMs(val) {
  if (!val) return null;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (typeof val.toDate  === 'function') return val.toDate().getTime();
  if (val._seconds != null)              return val._seconds * 1000;
  if (typeof val === 'number')           return val;
  if (typeof val === 'string') {
    const ms = new Date(val).getTime();
    return isNaN(ms) ? null : ms;
  }
  return null;
}

// ── Public status derivation ──────────────────────────────────────────────────

/**
 * Derives the public carousel's coarse status from the shared
 * getShowdownDisplayState lifecycle — never from stored status strings or a
 * second independent timing interpretation. 'closed' hides a game from the
 * public landing page entirely (results pending/completed/archived games
 * aren't "available to play" for a stranger); 'building' is landing-only
 * cosmetic ordering for games with no races configured yet.
 */
export function computePublicStatus(slate) {
  const state = getShowdownDisplayState(slate);
  if (!state) return 'closed';

  if (state.lifecycle === 'resultsPending' || state.lifecycle === 'completed' || state.lifecycle === 'archived') {
    return 'closed';
  }
  if (state.lifecycle === 'live' || state.lifecycle === 'entriesOpen') return 'live';
  if ((slate.totalRaces ?? 0) === 0) return 'building';
  return 'waiting';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCountdown(targetMs) {
  const diff = targetMs - Date.now();
  if (diff <= 0) return '0m';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 24) {
    const d  = Math.floor(h / 24);
    const rh = h % 24;
    return `${d}d ${rh}h`;
  }
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDateLabel(val) {
  const ms = toMs(val);
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long',
  }).toUpperCase();
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ShowdownCard({ showdown }) {
  // Force re-render every minute so countdowns stay fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const [cardRef, mobileInView] = useMobileInView();

  const now = Date.now();
  const openMs  = toMs(showdown.gameOpenTime);
  const closeMs = toMs(showdown.gameCloseTime);

  // Time-based status — derived strictly from timing fields, never from stored strings
  const isCurrentlyLive = !!(openMs && closeMs && now >= openMs && now < closeMs);
  const isLive          = isCurrentlyLive || !!(openMs && now >= openMs && (!closeMs || now < closeMs));
  const isOpeningSoon   = !!(openMs && now < openMs);
  const isClosed        = !!(closeMs && now >= closeMs);

  // Player count — try multiple possible field names
  const playerCount = showdown.activePlayerCount
    ?? showdown.totalEntriesSubmitted
    ?? showdown.playerCount
    ?? showdown.players
    ?? 0;
  const maxPlayers = showdown.maxPlayers || null;
  const isFull     = !!(maxPlayers && playerCount >= maxPlayers);

  // Tournament
  const isTournament   = showdown.gameMode === 'tournament' || !!showdown.tournamentId;
  const tournamentName = showdown.tournamentName || '';
  const roundNumber    = showdown.roundNumber || null;

  // Prizes
  const prizes    = showdown.prizes || [];
  const hasPrizes = prizes.length > 0;

  // Derive entries-open from time + flags — never from stored boolean alone.
  // pricesLocked: true is the sole signal that entries are open.
  // entriesOpen field is intentionally NOT used — it defaults to false on new
  // games and is never set to true by the current workflow, causing false negatives.
  const pricesReady        = showdown.pricesLocked === true;
  const entriesActuallyOpen =
    isLive &&     // game window is currently open
    pricesReady && // share prices are available to players
    !isFull;      // capacity not reached

  // Stable spend
  const stableSpend = showdown.totalBankroll
    ?? ((showdown.avgSpendPerRace ?? 0) * (showdown.totalRaces ?? 1));

  return (
    <div
      ref={cardRef}
      className={[
        'lp-showdown-card',
        isLive      ? 'lp-showdown-card--live'    : '',
        isClosed    ? 'lp-showdown-card--closed'  : '',
        mobileInView ? 'lp-showdown-card--in-view' : '',
      ].join(' ')}
    >

      {/* Image + overlay badges */}
      <div className="lp-showdown-card-image">
        <img src="/track-wp.png" alt="" />

        {isTournament && (
          <div className="lp-showdown-tournament-badge">
            🏆 {tournamentName || 'TOURNAMENT'}
            {roundNumber && <span> · Round {roundNumber}</span>}
          </div>
        )}

        <div className={`lp-showdown-status-badge lp-showdown-status-badge--${
          isLive ? 'live' : isOpeningSoon ? 'soon' : 'closed'
        }`}>
          {isLive ? '● LIVE' : isOpeningSoon ? '● OPENING SOON' : 'CLOSED'}
        </div>
      </div>

      {/* Body */}
      <div className="lp-showdown-card-body">

        <div className="lp-showdown-card-date">
          {formatDateLabel(showdown.gameOpenTime || showdown.meetingDate)}
        </div>

        <h3 className="lp-showdown-card-name">
          {(showdown.gameName || 'SHOWDOWN').toUpperCase()}
        </h3>

        {isOpeningSoon && openMs && (
          <div className="lp-showdown-countdown lp-showdown-countdown--soon">
            ⏱ Opens in {formatCountdown(openMs)}
          </div>
        )}
        {isLive && closeMs && (
          <div className="lp-showdown-countdown lp-showdown-countdown--live">
            ⏱ Closes in {formatCountdown(closeMs)}
          </div>
        )}

        <div className="lp-showdown-card-meta">
          {stableSpend > 0 && (
            <span className="lp-showdown-meta-item">
              🏆 ${stableSpend.toLocaleString('en-AU')} stable
            </span>
          )}
          {showdown.totalRaces > 0 && (
            <span className="lp-showdown-meta-item">
              🎯 {showdown.totalRaces} legs
            </span>
          )}
        </div>

        <div className="lp-showdown-players-row">
          <span className="lp-showdown-players-count">
            👥 {playerCount} {playerCount === 1 ? 'entry' : 'entries'}
          </span>
          <span className="lp-showdown-players-cap">
            {maxPlayers
              ? isFull
                ? <span className="lp-showdown-full">FULL</span>
                : `/ ${maxPlayers.toLocaleString('en-AU')} max`
              : '/ Unlimited'
            }
          </span>
        </div>

        {hasPrizes && (
          <div className="lp-showdown-prizes">
            <div className="lp-showdown-prizes-label">PRIZES</div>
            <div className="lp-showdown-prizes-list">
              {prizes.slice(0, 3).map((prize, i) => (
                <div key={prize.place ?? i} className="lp-showdown-prize-item">
                  <span className="lp-showdown-prize-place">
                    {prize.place === 1 ? '🥇'
                      : prize.place === 2 ? '🥈'
                      : prize.place === 3 ? '🥉'
                      : `${prize.place}th`}
                  </span>
                  <span className="lp-showdown-prize-desc">{prize.description}</span>
                </div>
              ))}
              {prizes.length > 3 && (
                <div className="lp-showdown-prize-more">
                  +{prizes.length - 3} more prizes
                </div>
              )}
            </div>
          </div>
        )}

        <div className="lp-showdown-entries-row">
          <span className={`lp-showdown-entries-badge lp-showdown-entries-badge--${
            entriesActuallyOpen ? 'open' : 'closed'
          }`}>
            🎫 Entries {entriesActuallyOpen ? 'Open' : 'Closed'}
          </span>
        </div>

        {/* Marketing CTA — always blue "PLAY NOW" on the public landing page */}
        <a href="/signin" className="lp-showdown-cta lp-showdown-cta--play">
          PLAY NOW
        </a>

      </div>
    </div>
  );
}

export default ShowdownCard;
